import { canAccessAdminShell, canManageRoles } from './adminRoles.js';

/**
 * Admin navigation registry.
 * `reserved: true` items are not rendered and have no mounted route.
 * Optional `children` render as nested sidebar links under the parent.
 *
 * @typedef {{
 *   id: string,
 *   labelKey: string,
 *   path: string,
 *   allowedRoles: string[],
 *   reserved?: boolean,
 *   end?: boolean,
 * }} AdminNavChild
 *
 * @typedef {{
 *   id: string,
 *   labelKey: string,
 *   path: string,
 *   icon: string,
 *   allowedRoles: string[],
 *   reserved: boolean,
 *   badgeSource: string | null,
 *   children?: AdminNavChild[],
 * }} AdminNavItem
 */

/** @type {AdminNavItem[]} */
export const ADMIN_NAV_ITEMS = [
  {
    id: 'dashboard',
    labelKey: 'admin.nav.dashboard',
    path: '/admin',
    icon: 'LayoutDashboard',
    allowedRoles: ['super_admin', 'admin', 'support'],
    reserved: false,
    badgeSource: null,
  },
  {
    id: 'feedback',
    labelKey: 'admin.nav.feedback',
    path: '/admin/feedback',
    icon: 'MessageSquareWarning',
    allowedRoles: ['super_admin', 'admin', 'support'],
    reserved: false,
    badgeSource: null,
  },
  {
    id: 'approvals',
    labelKey: 'admin.nav.approvals',
    path: '/admin/approvals',
    icon: 'ShieldCheck',
    allowedRoles: ['super_admin', 'admin', 'support'],
    reserved: false,
    badgeSource: null,
  },
  {
    id: 'settings',
    labelKey: 'admin.nav.settings',
    path: '/admin/settings',
    icon: 'Settings',
    allowedRoles: ['super_admin'],
    reserved: true,
    badgeSource: null,
  },
  {
    id: 'users',
    labelKey: 'admin.nav.users',
    path: '/admin/users',
    icon: 'Users',
    allowedRoles: ['super_admin', 'admin', 'support'],
    reserved: false,
    badgeSource: null,
  },
  {
    id: 'properties',
    labelKey: 'admin.nav.properties',
    path: '/admin/properties',
    icon: 'Building2',
    allowedRoles: ['super_admin', 'admin', 'support'],
    reserved: false,
    badgeSource: null,
  },
  {
    id: 'activity',
    labelKey: 'admin.nav.activity',
    path: '/admin/activity',
    icon: 'Activity',
    allowedRoles: ['super_admin', 'admin'],
    reserved: false,
    badgeSource: null,
  },
  {
    id: 'financing',
    labelKey: 'admin.nav.financing',
    path: '/admin/financing',
    icon: 'Banknote',
    allowedRoles: ['super_admin', 'admin'],
    reserved: true,
    badgeSource: null,
  },
  {
    id: 'market-data',
    labelKey: 'admin.nav.marketData',
    path: '/admin/market-data',
    icon: 'Globe',
    allowedRoles: ['super_admin', 'admin'],
    reserved: true,
    badgeSource: null,
  },
  {
    id: 'prospection',
    labelKey: 'admin.nav.prospection',
    path: '/admin/prospection',
    icon: 'MapPinned',
    allowedRoles: ['super_admin', 'admin'],
    reserved: false,
    badgeSource: null,
    children: [
      {
        id: 'prospection-prospects',
        labelKey: 'admin.nav.prospection.prospects',
        path: '/admin/prospection',
        allowedRoles: ['super_admin', 'admin'],
      },
      {
        id: 'prospection-follow-up',
        labelKey: 'admin.nav.prospection.followUp',
        path: '/admin/prospection/follow-up',
        allowedRoles: ['super_admin', 'admin'],
      },
      {
        id: 'prospection-import',
        labelKey: 'admin.nav.prospection.import',
        path: '/admin/prospection/import',
        allowedRoles: ['super_admin', 'admin'],
      },
      {
        id: 'prospection-campaigns',
        labelKey: 'admin.nav.prospection.campaigns',
        path: '/admin/prospection/campaigns',
        allowedRoles: ['super_admin', 'admin'],
      },
      {
        id: 'prospection-letter-templates',
        labelKey: 'admin.nav.prospection.letterTemplates',
        path: '/admin/prospection/letter-templates',
        allowedRoles: ['super_admin', 'admin'],
      },
    ],
  },
  {
    id: 'audit',
    labelKey: 'admin.nav.audit',
    path: '/admin/audit',
    icon: 'ScrollText',
    allowedRoles: ['super_admin', 'admin'],
    reserved: false,
    badgeSource: null,
  },
];

/**
 * Prospects list + Prospect 360 — not import/campaigns.
 * @param {string} pathname
 */
export function isProspectionProspectsPath(pathname) {
  const path = String(pathname || '');
  if (path === '/admin/prospection') return true;
  if (!path.startsWith('/admin/prospection/')) return false;
  const rest = path.slice('/admin/prospection/'.length);
  if (!rest || rest === 'import' || rest.startsWith('import/')) return false;
  if (rest === 'follow-up' || rest.startsWith('follow-up/')) return false;
  if (rest === 'campaigns' || rest.startsWith('campaigns/')) return false;
  if (rest === 'letter-templates' || rest.startsWith('letter-templates/')) return false;
  return true;
}

/**
 * @param {AdminNavChild} child
 * @param {string} pathname
 */
export function isAdminNavChildActive(child, pathname) {
  const path = String(pathname || '');
  if (!child) return false;
  if (child.id === 'prospection-prospects') return isProspectionProspectsPath(path);
  if (child.id === 'prospection-follow-up') {
    return path === '/admin/prospection/follow-up' || path.startsWith('/admin/prospection/follow-up/');
  }
  if (child.id === 'prospection-import') {
    return path === '/admin/prospection/import' || path.startsWith('/admin/prospection/import/');
  }
  if (child.id === 'prospection-campaigns') {
    return path === '/admin/prospection/campaigns' || path.startsWith('/admin/prospection/campaigns/');
  }
  if (child.id === 'prospection-letter-templates') {
    return (
      path === '/admin/prospection/letter-templates' ||
      path.startsWith('/admin/prospection/letter-templates/')
    );
  }
  if (child.end) return path === child.path;
  return path === child.path || path.startsWith(`${child.path}/`);
}

/**
 * Parent section is active when any child matches, or the path is under the parent.
 * @param {AdminNavItem} item
 * @param {string} pathname
 */
export function isAdminNavSectionActive(item, pathname) {
  const path = String(pathname || '');
  if (!item) return false;
  const children = Array.isArray(item.children) ? item.children : [];
  if (children.length > 0) {
    return children.some((child) => isAdminNavChildActive(child, path));
  }
  if (item.path === '/admin') return path === '/admin';
  return path === item.path || path.startsWith(`${item.path}/`);
}

/**
 * @param {string | null | undefined} role
 * @returns {AdminNavItem[]}
 */
export function visibleAdminNavItems(role) {
  if (!canAccessAdminShell(role)) return [];
  return ADMIN_NAV_ITEMS.filter((item) => {
    if (item.reserved) return false;
    if (item.id === 'settings' && !canManageRoles(role)) return false;
    return item.allowedRoles.includes(role);
  }).map((item) => {
    const children = Array.isArray(item.children)
      ? item.children.filter(
          (child) => !child.reserved && Array.isArray(child.allowedRoles) && child.allowedRoles.includes(role),
        )
      : undefined;
    if (!children) return item;
    return { ...item, children };
  });
}

/**
 * Action Center static metadata (counts come from getAdminDashboardSummary.actions).
 * `countKey` must match ADMIN_DASHBOARD_ACTION_KEYS in functions/admin/dashboard.js.
 * @typedef {{
 *   id: string,
 *   module: string,
 *   titleKey: string,
 *   href: string,
 *   severity: 'info' | 'warning' | 'critical',
 *   allowedRoles: string[],
 *   countKey: 'pendingApprovals' | 'newFeedback',
 * }} AdminActionSource
 */

/** @type {AdminActionSource[]} */
export const ADMIN_ACTION_SOURCES = [
  {
    id: 'pending-approvals',
    module: 'approvals',
    titleKey: 'admin.nav.approvals',
    href: '/admin/approvals',
    severity: 'warning',
    allowedRoles: ['super_admin', 'admin', 'support'],
    countKey: 'pendingApprovals',
  },
  {
    id: 'new-feedback',
    module: 'feedback',
    titleKey: 'admin.nav.feedback',
    href: '/admin/feedback',
    severity: 'info',
    allowedRoles: ['super_admin', 'admin', 'support'],
    countKey: 'newFeedback',
  },
];
